import React, { useState, useRef } from "react";
import {
  Box,
  Button,
  Flex,
  Image,
  Input,
  Text,
  VStack,
  useToast,
  Spinner,
  Center,
  Icon,
  HStack,
  Badge,
  useColorMode,
} from "@chakra-ui/react";
import {
  AttachmentIcon,
  CloseIcon,
  CheckIcon,
  DownloadIcon,
} from "@chakra-ui/icons";
import { uploadDocument } from "../libs/qazbilim-backend";
import { useTranslation } from "react-i18next";
import LoadingLogo from "./LoadingLogo";

interface ImageUploadProps {
  onImageSelect: (file: File) => void;
  onImageUpload?: (imageUrl: string) => void;
  existingImageUrl?: string;
  helpText?: string;
  bookTitle?: string;
  isUploading?: boolean;
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  onImageSelect,
  onImageUpload,
  existingImageUrl,
  helpText = "JPG, PNG, GIF up to 10MB",
  bookTitle = "",
  isUploading = false,
}) => {
  const { t } = useTranslation();
  const { colorMode } = useColorMode();
  const [preview, setPreview] = useState<string | null>(
    existingImageUrl || null
  );
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();

  const processFile = async (file: File) => {
    // Check if the file is an image
    if (!file.type.startsWith("image/")) {
      toast({
        title: t("imageUpload.invalidFileType"),
        description: t("imageUpload.invalidFileTypeDescription"),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Create a preview
    const fileReader = new FileReader();
    fileReader.onload = () => {
      if (typeof fileReader.result === "string") {
        setPreview(fileReader.result);
      }
    };
    fileReader.readAsDataURL(file);

    // Pass the file to the parent component
    onImageSelect(file);
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await processFile(file);
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const removeImage = () => {
    setPreview(null);
    onImageSelect(null as unknown as File); // Signal no file selected
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Drag and drop handlers
  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      await processFile(file);
    }
  };

  return (
    <VStack spacing={4} align="stretch" width="100%">
      <Input
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        ref={fileInputRef}
        display="none"
      />

      {preview ? (
        <Box
          position="relative"
          borderRadius="md"
          overflow="hidden"
          bg={
            colorMode === "light"
              ? "rgba(240, 240, 240, 0.6)"
              : "rgba(30, 35, 45, 0.6)"
          }
          height="250px"
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          <Image
            src={preview}
            alt="Cover preview"
            objectFit="contain"
            maxWidth="100%"
            maxHeight="250px"
            borderRadius="md"
          />
          <Button
            position="absolute"
            top="2"
            right="2"
            size="sm"
            colorScheme="red"
            onClick={removeImage}
            borderRadius="full"
            width="30px"
            height="30px"
            minWidth="30px"
            padding="0"
            variant="ghost"
          >
            <CloseIcon boxSize="12px" />
          </Button>
        </Box>
      ) : (
        <Flex
          borderRadius="md"
          height="250px"
          justify="center"
          align="center"
          bg={
            isDragging
              ? colorMode === "light"
                ? "blue.50"
                : "rgba(40, 50, 70, 0.6)"
              : colorMode === "light"
              ? "rgba(240, 240, 240, 0.6)"
              : "rgba(30, 35, 45, 0.6)"
          }
          _hover={{
            bg:
              colorMode === "light"
                ? "rgba(235, 235, 235, 0.7)"
                : "rgba(35, 40, 50, 0.7)",
            cursor: "pointer",
          }}
          onClick={handleButtonClick}
          boxShadow={isDragging ? "0 0 0 2px blue.500" : "sm"}
          border={isDragging ? "2px dashed blue.500" : "none"}
          transition="all 0.2s"
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {isUploading ? (
            <Center flexDirection="column">
              <LoadingLogo size="lg" mb={2} />
              <Text>{t("imageUpload.uploading")}</Text>
            </Center>
          ) : (
            <Center flexDirection="column">
              {isDragging ? (
                <>
                  <Icon
                    as={DownloadIcon}
                    boxSize="32px"
                    mb={2}
                    color="blue.500"
                  />
                  <Text fontWeight="medium" color="blue.500">
                    {t("imageUpload.dropHere")}
                  </Text>
                </>
              ) : (
                <>
                  <Icon as={AttachmentIcon} boxSize="32px" mb={2} />
                  <Text fontWeight="medium">
                    {t("imageUpload.clickOrDrag")}
                  </Text>
                  <Text
                    fontSize="sm"
                    color={colorMode === "light" ? "gray.500" : "gray.300"}
                  >
                    {helpText}
                  </Text>
                </>
              )}
            </Center>
          )}
        </Flex>
      )}

      {!isUploading && !preview && (
        <Button
          leftIcon={<AttachmentIcon />}
          onClick={handleButtonClick}
          variant="outline"
          width="100%"
        >
          {t("imageUpload.selectImage")}
        </Button>
      )}

      {preview && bookTitle && (
        <Box
          p={2}
          bg={colorMode === "light" ? "teal.500" : "teal.600"}
          color="white"
          borderRadius="md"
          display="flex"
          alignItems="center"
        >
          <CheckIcon mr={2} />
          <Text fontWeight="medium" flex="1">
            {bookTitle}
          </Text>
          <Badge colorScheme="green" variant="solid">
            {isUploading
              ? t("imageUpload.uploading")
              : t("imageUpload.readyToUpload")}
          </Badge>
        </Box>
      )}
    </VStack>
  );
};

export default ImageUpload;
